import { Widget } from '../core/Widget.js';
import { Validation } from '../core/Validation.js';

const UNITS = ["B", "KB", "MB", "GB", "TB", "PB"];
const MAX_INT64 = 9223372036854775807n;
class FilesizeWidget extends Widget {
    constructor() {
        super(...arguments);
        this.unbinders = [];
    }
    static _cssKey() { return "im-filesize"; }
    _create(op) {
        // resolve elements
        this.unitEl =
            op.unitSelect ??
                (this.element.nextElementSibling instanceof HTMLSelectElement
                    ? this.element.nextElementSibling
                    : undefined);
        this.bytesEl =
            op.bytesInput ??
                (this.element.previousElementSibling instanceof HTMLInputElement
                    ? this.element.previousElementSibling
                    : undefined);
        if (!this.unitEl) {
            throw new Error("[FilesizeWidget] unit <select> не найден (ожидается nextElementSibling или options.unitSelect).");
        }
        if (!this.bytesEl) {
            throw new Error("[FilesizeWidget] bytes <input> не найден (ожидается previousElementSibling или options.bytesInput).");
        }
        this.fixedBytes = op.fixedBytes ?? true;
        const sc = Number(op.scale);
        this.scale = Math.min(Number.isFinite(sc) && sc >= 0 ? sc : 2, 10);
        // handlers
        const onValueInput = () => this._onValueChanged();
        const onValueChange = () => this._onValueChanged();
        const onUnitChange = () => this._onUnitChanged();
        this.element.addEventListener("input", onValueInput);
        this.element.addEventListener("change", onValueChange);
        this.unitEl.addEventListener("change", onUnitChange);
        this.unbinders.push(() => this.element.removeEventListener("input", onValueInput));
        this.unbinders.push(() => this.element.removeEventListener("change", onValueChange));
        this.unbinders.push(() => this.unitEl?.removeEventListener("change", onUnitChange));
        // первичный пересчёт/валидация
        this._syncFromCurrentState();
    }
    // ============== Validatable ==============
    validate() {
        const raw = (this.element.value ?? "").trim();
        if (raw === "") {
            // пустое — валидно, но байты чистим
            return { ok: true };
        }
        const unit = this._getUnit();
        if (!unit)
            return { ok: false };
        return { ok: this._parseVisibleBytes(raw, unit) !== null };
    }
    // ============== WithMainValue ==============
    /** Возвращает точное Int64-значение байтов как десятичную строку или null */
    getValue() {
        const bytes = this._readBytes();
        return bytes?.toString() ?? null;
    }
    /**
     * Принимает байты и выставляет видимое значение в текущей единице.
     * Int64 вне безопасного диапазона JavaScript передаётся строкой или bigint.
     * null/undefined → очищает обе стороны.
     */
    setValue(val) {
        if (val == null || (typeof val === "string" && val.trim() === "")) {
            this.element.value = "";
            if (this.bytesEl)
                this.bytesEl.value = "";
            Validation.run(this.element);
            return true;
        }
        const bytes = this._parseBytesValue(val);
        if (bytes === null) {
            if (this.bytesEl)
                this.bytesEl.value = "";
            Validation.run(this.element);
            return true;
        }
        if (this.bytesEl)
            this.bytesEl.value = bytes.toString();
        const best = this._pickBestUnit(bytes);
        if (this.unitEl)
            this.unitEl.value = best;
        const factor = this._factor(best);
        this.element.value = this._formatBytes(bytes, factor);
        Validation.run(this.element);
        return true;
    }
    /** Пустая строка → false; "0" → true (как и у Numbox) */
    hasValue() {
        return (this.bytesEl?.value ?? "") !== "";
    }
    // ============== Handlers ==============
    _onValueChanged() {
        // нормализация ',' → '.'
        const raw = (this.element.value ?? "").replaceAll(",", ".").trim();
        this.element.value = raw;
        if (raw === "") {
            if (this.bytesEl)
                this.bytesEl.value = "";
            Validation.run(this.element);
            return;
        }
        const unit = this._getUnit();
        if (!unit) {
            if (this.bytesEl)
                this.bytesEl.value = "";
            Validation.run(this.element);
            return;
        }
        const bytes = this._parseVisibleBytes(raw, unit);
        if (bytes === null) {
            if (this.bytesEl)
                this.bytesEl.value = "";
            Validation.run(this.element);
            return;
        }
        const factor = this._factor(unit);
        if (this.bytesEl)
            this.bytesEl.value = bytes.toString();
        this.element.value = this._formatBytes(bytes, factor);
        Validation.run(this.element);
    }
    _onUnitChanged() {
        // если фиксируем байты — меняем видимое значение под новые единицы
        // иначе — просто перерассчитаем байты из текущего видимого
        if (this.fixedBytes) {
            const bytes = this._readBytes();
            if (bytes === null) {
                // если байтов нет — поведение как при пустом
                Validation.run(this.element);
                return;
            }
            const unit = this._getUnit() ?? "B";
            const factor = this._factor(unit);
            this.element.value = this._formatBytes(bytes, factor);
            // bytesEl уже хранит байты — их не трогаем
            Validation.run(this.element);
        }
        else {
            this._onValueChanged();
        }
    }
    _syncFromCurrentState() {
        const bytes = this._readBytes();
        if (bytes !== null) {
            const best = this._pickBestUnit(bytes);
            if (this.unitEl)
                this.unitEl.value = best;
            const factor = this._factor(best);
            this.element.value = this._formatBytes(bytes, factor);
            Validation.run(this.element);
            return;
        }
        this._onValueChanged();
    }
    // ============== Utils ==============
    _getUnit() {
        const u = (this.unitEl?.value ?? "").toUpperCase();
        return UNITS.includes(u) ? u : null;
    }
    _pickBestUnit(bytes) {
        // Порог PB совпадает с серверным TextTools.FileSize | 2026-08-26
        if (bytes < this._factor("KB"))
            return "B";
        if (bytes < this._factor("MB"))
            return "KB";
        if (bytes < this._factor("GB"))
            return "MB";
        if (bytes < this._factor("TB"))
            return "GB";
        if (bytes < 10n * this._factor("PB"))
            return "TB";
        return "PB";
    }
    _factor(u) {
        switch (u) {
            case "B": return 1n;
            case "KB": return 1024n;
            case "MB": return 1024n ** 2n;
            case "GB": return 1024n ** 3n;
            case "TB": return 1024n ** 4n;
            case "PB": return 1024n ** 5n;
        }
    }
    // Десятичная строка сохраняет Int64 без потери точности в JSON | 2026-08-26
    _readBytes() {
        const raw = (this.bytesEl?.value ?? "").trim();
        if (!/^\d+$/u.test(raw))
            return null;
        const bytes = BigInt(raw);
        return bytes <= MAX_INT64 ? bytes : null;
    }
    // Небезопасный number уже потерял исходную точность и не принимается | 2026-08-26
    _parseBytesValue(val) {
        if (typeof val === "bigint") {
            return val >= 0n && val <= MAX_INT64 ? val : null;
        }
        if (typeof val === "number") {
            if (!Number.isFinite(val) || val < 0)
                return null;
            const rounded = Math.round(val);
            if (!Number.isSafeInteger(rounded)) {
                throw new RangeError("[FilesizeWidget] Int64 вне безопасного диапазона нужно передавать строкой или bigint.");
            }
            return BigInt(rounded);
        }
        return this._parseDecimalBytes(val.trim(), 1n);
    }
    // Пересчёт десятичного ввода в байты выполняется без промежуточного number | 2026-08-26
    _parseVisibleBytes(raw, unit) {
        return this._parseDecimalBytes(raw, this._factor(unit));
    }
    _parseDecimalBytes(raw, factor) {
        const match = /^(\d*)(?:\.(\d*))?$/u.exec(raw);
        if (!match || (!match[1] && !match[2]))
            return null;
        const whole = BigInt(match[1] || "0");
        const fraction = match[2] || "";
        const denominator = 10n ** BigInt(fraction.length);
        const fractionValue = fraction ? BigInt(fraction) : 0n;
        const numerator = (whole * denominator + fractionValue) * factor;
        const bytes = (numerator + denominator / 2n) / denominator;
        return bytes <= MAX_INT64 ? bytes : null;
    }
    // Форматирование видимого значения не переводит Int64 в number | 2026-08-26
    _formatBytes(bytes, factor) {
        const scaleFactor = 10n ** BigInt(this.scale);
        const scaled = (bytes * scaleFactor + factor / 2n) / factor;
        if (this.scale === 0)
            return scaled.toString();
        const digits = scaled.toString().padStart(this.scale + 1, "0");
        const int = digits.slice(0, -this.scale);
        const fraction = digits.slice(-this.scale).replace(/0+$/u, "");
        return fraction ? `${int}.${fraction}` : int;
    }
    _onDestroy() {
        for (const u of this.unbinders) {
            try {
                u();
            }
            catch { /* ignore */ }
        }
        this.unbinders.length = 0;
    }
}
/** Имя виджета */
FilesizeWidget._alias = "Filesize";

export { FilesizeWidget };
